/**
 * AuthGuard Component
 *
 * Handles the Notice ID + Confirmation Code authentication flow for the DOE settlement.
 * After successful authentication, redirects users to the FPA settlement site.
 *
 * Single-step inline flow (no modals, no OTP):
 *   Notice ID + Confirmation Code are validated via `classMemberPost`; on a
 *   successful response the user is redirected to the FPA settlement site.
 *
 * @module AuthGuard
 * @since 1.0.0
 */

import { createElement } from "commonAssets/lib/createElement";
import { validateField } from "commonAssets/lib/validateField";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError";
import { loadingGif } from "commonAssets/lib/loadingGif.js";
import {
    loading,
    appConfigDev,
    appConfigKey,
    appConfigProd,
} from "commonAssets/js/constants";

export class AuthGuard {
    /**
     * @param {Object} options - Configuration options
     * @param {Object} options.i18n - Internationalization instance
     * @param {Object} options.errorHandler - Error handler instance
     * @param {string} options.authDomain - Domain used for API payloads so the
     *   deadline gate and class_member lookup read this site's own
     *   `domain_info` row (e.g., "www.doesettlement.com").
     * @param {string} options.targetDomain - Domain to redirect to on
     *   successful authentication (e.g., "www.fpasettlement.com").
     */
    constructor({ i18n, errorHandler, authDomain, targetDomain }) {
        this.i18n = i18n;
        this.errorHandler = errorHandler;
        this.authDomain = authDomain;
        this.targetDomain = targetDomain;
        this.api = null;
        this._step1HTML = "";
    }

    /**
     * Initialize the AuthGuard component
     * Sets up the API client and renders the form
     */
    init() {
        this._initApiClient();
        this._renderForm();
        this._bindEvents();

        // Save Step 1 content for Reset
        this._step1HTML = this._getContainer().innerHTML;
    }

    /**
     * Initialize the API client based on environment
     * @private
     */
    _initApiClient() {
        const hostname = window.location.hostname;
        let env = appConfigProd;

        if (
            hostname.startsWith("local") ||
            hostname === "localhost" ||
            hostname.split(".")[1] === "angeiontest"
        ) {
            env = appConfigDev;
        }

        this.api = apigClientFactory.newClient({
            apiKey: appConfigKey,
            url: env,
        });
    }

    /**
     * Create and render the authentication form (Step 1)
     * @private
     */
    _renderForm() {
        const formElement = createElement(
            "fieldset",
            {
                class: "col-md-6 mb-5 offset-md-3 mt-5",
                id: "notice-confirmation",
            },
            [
                createElement(
                    "div",
                    {
                        class: "bg-light text-dark border rounded p-3",
                        id: "auth-content",
                    },
                    [
                        createElement(
                            "p",
                            { class: "mb-3" },
                            [],
                            'If you received a personalized notice in the mail or via email with a <strong class="underline">Notice ID</strong> and <strong class="underline">Confirmation Code</strong>, please enter the codes you were provided below. <br><br> Please remember to enter the full Notice ID exactly as it appears on your personalized Notice, (i.e. 12345678).'
                        ),
                        createElement("div", { class: "mb-3" }, [
                            createElement("input", {
                                type: "text",
                                name: "zero_zero_id",
                                class: "form-control",
                                id: "zero_zero_id",
                                "data-validate": "required",
                                required: "",
                            }),
                            createElement(
                                "label",
                                { for: "zero_zero_id" },
                                [],
                                'Notice ID <span class="error"></span>'
                            ),
                        ]),
                        createElement("div", { class: "mb-3" }, [
                            createElement("input", {
                                type: "text",
                                name: "confirmation_code",
                                class: "form-control",
                                id: "confirmation_code",
                                "data-validate": "required",
                                required: "",
                            }),
                            createElement(
                                "label",
                                { for: "confirmation_code" },
                                [],
                                'Confirmation Code <span class="error"></span>'
                            ),
                        ]),
                        createElement("div", {}, [
                            createElement("input", {
                                type: "submit",
                                class: "btn btn-primary",
                                id: "submit-guard",
                                value: "Submit",
                            }),
                        ]),
                    ]
                ),
            ]
        );

        document.getElementById("output").appendChild(formElement);
    }

    /**
     * Get the auth content container element
     * @returns {HTMLElement}
     * @private
     */
    _getContainer() {
        return document.getElementById("auth-content");
    }

    /**
     * Transition the container content with a fade effect
     * @param {string} html - New HTML content
     * @param {Function} [afterSwap] - Callback to run after content is swapped (for binding events)
     * @private
     */
    _transitionTo(html, afterSwap) {
        const container = this._getContainer();
        container.classList.add("fade-out");

        setTimeout(() => {
            container.innerHTML = html;
            container.classList.remove("fade-out");

            if (afterSwap) afterSwap();
        }, 500);
    }

    /**
     * Show an inline alert within the current step
     * @param {string} message - Alert message (plain text, escaped for safety)
     * @param {Object} [options] - Options
     * @param {boolean} [options.tryAgain] - Show a "Try Again" button that returns to Step 1
     * @private
     */
    _showInlineAlert(message, options = {}) {
        const alertDiv = document.createElement("div");
        alertDiv.className = "alert alert-danger";
        alertDiv.setAttribute("role", "alert");

        const messagePara = document.createElement("p");
        messagePara.textContent = message;
        alertDiv.appendChild(messagePara);

        if (options.tryAgain) {
            const btnWrapper = document.createElement("div");
            btnWrapper.className = "d-flex gap-2";

            const tryAgainBtn = document.createElement("button");
            tryAgainBtn.type = "button";
            tryAgainBtn.className = "btn btn-outline-danger btn-sm";
            tryAgainBtn.textContent = "Try Again";
            tryAgainBtn.addEventListener("click", () => this._resetFlow());

            btnWrapper.appendChild(tryAgainBtn);
            alertDiv.appendChild(btnWrapper);
        }

        this._transitionTo("", () => {
            this._getContainer().appendChild(alertDiv);
        });
    }

    /**
     * Reset the flow back to Step 1
     * @private
     */
    _resetFlow() {
        this._transitionTo(this._step1HTML, () => {
            this._bindEvents();
        });
    }

    /**
     * Bind event listeners for Step 1
     * @private
     */
    _bindEvents() {
        document
            .getElementById("submit-guard")
            .addEventListener("click", (e) => this._handleSubmit(e));
    }

    /**
     * Handle form submission (Step 1)
     * @param {Event} e - Click event
     * @private
     */
    _handleSubmit(e) {
        e.preventDefault();

        const zero_zero_id = document.getElementById("zero_zero_id");
        const confirmation_code = document.getElementById("confirmation_code");

        let valid = true;
        const member = {
            domain: this.authDomain,
            zero_zero_id: zero_zero_id.value.toUpperCase(),
            confirmation_code: confirmation_code.value,
        };

        // Validate guard fields
        [zero_zero_id, confirmation_code].forEach((input) => {
            valid = validateField(input) && valid;
        });

        if (!valid) return;

        // Store validated credentials for FPA redirect handoff
        this._z = member.zero_zero_id;
        this._c = member.confirmation_code;

        loadingGif(loading, "show");
        this._verifyClassMember(member, zero_zero_id, confirmation_code);
    }

    /**
     * Verify class member via API
     * @param {Object} member - Member data
     * @param {HTMLElement} zero_zero_id - Notice ID input element
     * @param {HTMLElement} confirmation_code - Confirmation code input element
     * @private
     */
    _verifyClassMember(member, zero_zero_id, confirmation_code) {
        this.api
            .classMemberPost({}, member)
            .then((result) => {
                // Check for error message
                if (result.data.error) {
                    toggleFieldError(zero_zero_id, true, "");
                    toggleFieldError(confirmation_code, true, "");
                    loadingGif(loading, "hide", 500);
                    this._showInlineAlert(result.data.error, { tryAgain: true });
                    return;
                }

                // Valid class member — OTP flow removed (WP-1439);
                // redirect straight to FPA using the Step 1 credentials.
                this._redirectToFPA();
            })
            .catch((error) => {
                loadingGif(loading, "hide", 500);
                this.errorHandler.log_exception(
                    new Error("Error loading class member", { cause: error })
                );
                this._showInlineAlert(
                    this.i18n.getTranslation("claim_processing_error"),
                    { tryAgain: true }
                );
            });
    }

    /**
     * Redirect to the FPA settlement site
     * @private
     */
    _redirectToFPA() {
        const fragment = `z=${encodeURIComponent(this._z)}&c=${encodeURIComponent(this._c)}`;
        window.location.href = `https://${this.targetDomain}#${fragment}`;
    }
}
